import { ITEM_ORDER, ITEMS, SHOP, sellPrice, type HeroState, type ItemStats, type Simulation } from '@moba/shared';
import { itemIconUrl } from './itemIcons.ts';

import { t } from './i18n.ts';
/** Cómo se describe cada estadística de un objeto. */
const STAT_TEXT: Record<keyof ItemStats, (v: number) => string> = {
  maxHp: (v) => `+${v} ${t('Vida')}`,
  hpRegen: (v) => `+${v.toFixed(1)} ${t('Regeneración/s')}`,
  attackDamage: (v) => `+${v} ${t('Daño')}`,
  attackSpeed: (v) => `+${Math.round(v * 100)}% ${t('Vel. ataque')}`,
  armor: (v) => `+${v} ${t('Armadura')}`,
  abilityHaste: (v) => `+${v} ${t('Acel. habilidad')}`,
  moveSpeed: (v) => `+${v.toFixed(1)} ${t('Vel. movimiento')}`,
  omnivamp: (v) => `+${Math.round(v * 100)}% ${t('Omnisucción')}`,
  lethality: (v) => `+${v} ${t('Letalidad')}`,
  armorPen: (v) => `+${Math.round(v * 100)}% ${t('Pen. armadura')}`,
  tenacity: (v) => `+${Math.round(v * 100)}% ${t('Tenacidad')}`,
};

export function describeItem(id: string): string {
  const item = ITEMS[id];
  if (!item) return '';
  return (Object.entries(item.stats) as Array<[keyof ItemStats, number]>).map(([k, v]) => STAT_TEXT[k](v)).join(' · ');
}

/** Imágenes de objetos renderizadas desde su modelo 3D (p. ej. el caparazón de Koopa). */
const ITEM_IMAGES = new Map<string, string>();

export function setItemImage(id: string, url: string): void {
  ITEM_IMAGES.set(id, url);
}

/** Icono de objeto: su modelo 3D renderizado (si lo tiene) o su ilustración SVG. */
export function itemIcon(id: string, el: HTMLElement): void {
  const item = ITEMS[id];
  const image = ITEM_IMAGES.get(id);
  const art = itemIconUrl(id);
  el.textContent = item && !image && !art ? item.short : '';
  el.style.background = !item
    ? ''
    : image
      ? `center / 92% no-repeat url(${image}), radial-gradient(circle at 35% 30%, ${item.color}88, #0d1520)`
      : art
        ? `center / cover no-repeat url("${art}")`
        : `linear-gradient(140deg, ${item.color}, #1a2533)`;
  el.title = item ? `${t(item.name)}\n${describeItem(id)}` : '';
}

/**
 * Panel de la tienda (tecla I o botón del HUD). Se compra sólo en la base o
 * muerto; clic en un objeto del inventario lo vende por el 70%.
 */
export class Shop {
  private readonly root = document.getElementById('shop') as HTMLElement;
  private readonly goldEl = document.getElementById('shop-gold') as HTMLElement;
  private readonly statusEl = document.getElementById('shop-status') as HTMLElement;
  private readonly cards = new Map<string, { root: HTMLElement; button: HTMLButtonElement }>();
  private readonly inventory: HTMLButtonElement[] = [];

  constructor(
    private readonly onBuy: (itemId: string) => void,
    private readonly onSell: (slot: number) => void,
  ) {
    const grid = document.getElementById('shop-items') as HTMLElement;
    for (const id of ITEM_ORDER) {
      const item = ITEMS[id];
      const card = document.createElement('div');
      card.className = `shop-item${item.complete ? ' complete' : ''}`;
      card.innerHTML = `
        <span class="item-icon"></span>
        <div class="item-info">
          <div class="item-name">${t(item.name)}${item.complete ? `<span class="item-tag">${t('FINAL')}</span>` : ''}</div>
          <div class="item-stats">${describeItem(id)}</div>
        </div>
        <button class="item-buy" type="button"><span class="coin" aria-hidden="true"></span>${item.cost}</button>`;
      itemIcon(id, card.querySelector('.item-icon') as HTMLElement);
      const button = card.querySelector('.item-buy') as HTMLButtonElement;
      button.addEventListener('click', () => this.onBuy(id));
      grid.appendChild(card);
      this.cards.set(id, { root: card, button });
    }
    const inv = document.getElementById('shop-inventory') as HTMLElement;
    for (let i = 0; i < SHOP.slots; i++) {
      const slot = document.createElement('button');
      slot.className = 'inv-slot';
      slot.addEventListener('click', () => this.onSell(i));
      inv.appendChild(slot);
      this.inventory.push(slot);
    }
    document.getElementById('shop-close')?.addEventListener('click', () => this.setOpen(false));
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  setOpen(open: boolean): void {
    this.root.hidden = !open;
  }

  toggle(): void {
    this.setOpen(!this.isOpen);
  }

  update(sim: Simulation, player: HeroState): void {
    if (!this.isOpen) return;
    const canShop = sim.canShop(player);
    const full = player.items.length >= SHOP.slots;
    this.goldEl.textContent = String(player.gold);
    this.statusEl.textContent = !canShop
      ? t('Vuelve a tu base para comprar (B para volver)')
      : full
        ? t('Inventario lleno: vende un objeto para comprar otro')
        : t('Estás en la tienda');
    this.statusEl.classList.toggle('warn', !canShop || full);
    for (const [id, card] of this.cards) {
      const affordable = player.gold >= ITEMS[id].cost;
      const buyable = canShop && !full && affordable;
      card.button.disabled = !buyable;
      card.button.title = buyable
        ? t('Comprar {item}', { item: t(ITEMS[id].name) })
        : !canShop
          ? t('Sólo se compra en tu base')
          : full
            ? t('Inventario lleno')
            : t('Te faltan {gold} de oro', { gold: ITEMS[id].cost - player.gold });
      card.root.classList.toggle('unaffordable', !affordable);
      card.root.classList.toggle('buyable', buyable);
    }
    this.inventory.forEach((slot, i) => {
      const id = player.items[i];
      if (id) {
        itemIcon(id, slot);
        slot.title = `${t('{item} — vender por {gold}', { item: t(ITEMS[id].name), gold: sellPrice(id) })}\n${describeItem(id)}`;
        slot.disabled = !canShop;
      } else {
        slot.textContent = '';
        slot.style.background = '';
        slot.title = '';
        slot.disabled = true;
      }
    });
  }
}
